import React, { useState, useEffect } from 'react';
import { Search, Filter, Users, Phone, Mail, MapPin, Building2, Hash, Download } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import MainService from '../services/mainservice';
import * as XLSX from 'xlsx';

const MyCustomers = () => {
  const { showError, showSuccess, showWarning } = useToast();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [emirateFilter, setEmirateFilter] = useState('all');
  const [emirates, setEmirates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load emirates from API
  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      if (response && response.length > 0) {
        setEmirates(response);
      }
    } catch (error) {
      console.error('Error loading emirates:', error);
    }
  };

  // Load customers from API
  const loadCustomers = async () => {
    try {
      setLoading(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      const response = await MainService.CustomersInGridInPortal(userData.ClientId);
      
      if (response && response.length > 0) {
        setCustomers(response);
        setFilteredCustomers(response);
        showSuccess(`Loaded ${response.length} customers successfully`);
      } else {
        setCustomers([]);
        setFilteredCustomers([]);
        showWarning('No customers found');
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      showError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmirates();
    loadCustomers();
  }, []);

  // Filter customers based on search keyword and emirate
  useEffect(() => {
    let filtered = customers;
    
    // Filter by search keyword
    if (searchKeyword.trim()) {
      filtered = filtered.filter(customer => 
        (customer.Name || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (customer.MobileNo1 || '').includes(searchKeyword) ||
        (customer.MobileNo2 || '').includes(searchKeyword) ||
        (customer.TelNo || '').includes(searchKeyword) ||
        (customer.EmailId || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (customer.LocationName || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (customer.EmirateName || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (customer.Address || '').toLowerCase().includes(searchKeyword.toLowerCase())
      );
    }
    
    // Filter by emirate
    if (emirateFilter !== 'all') {
      filtered = filtered.filter(customer => (customer.EmirateName || '') === emirateFilter);
    }
    
    setFilteredCustomers(filtered);
  }, [searchKeyword, emirateFilter, customers]);

  const handleSearchInput = (e) => {
    setSearchKeyword(e.target.value);
  };

  const clearSearch = () => {
    setSearchKeyword('');
  };

  const handleSearch = () => {
    // Reload customers with current filters
    loadCustomers();
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredCustomers.map(customer => ({
        'Customer Name': customer.Name || 'N/A',
        'Mobile No1': customer.MobileNo1 || 'N/A',
        'Mobile No2': customer.MobileNo2 || 'N/A',
        'Tel No': customer.TelNo || 'N/A',
        'Email Id': customer.EmailId || 'N/A',
        'Address': customer.Address || 'N/A',
        'PO Box': customer.POBOX || 'N/A',
        'Location': customer.LocationName || 'N/A',
        'Emirate': customer.EmirateName || 'N/A',
        'Location URL': customer.LocationUrl || 'N/A',
        'Remarks': customer.Remarks || 'N/A',
        'Date': customer.Date ? new Date(customer.Date).toLocaleDateString('en-GB') : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customers');
      
      const fileName = `customers_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showSuccess('Customers exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export customers to Excel');
    }
  };

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Customers</h1>
          <p className="text-gray-600">Manage and view your customer information</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchKeyword}
                  onChange={handleSearchInput}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                />
                {searchKeyword && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <select
                  value={emirateFilter}
                  onChange={(e) => setEmirateFilter(e.target.value)}
                  className="w-40 px-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                >
                  <option value="all">All Emirates</option>
                  {emirates.map((emirate) => (
                    <option key={emirate.Id || emirate.id} value={emirate.Name || emirate.name}>
                      {emirate.Name || emirate.name}
                    </option>
                  ))}
                </select>
              </div>
             
              <button 
                onClick={exportToExcel}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 transition-colors duration-200 shadow-sm"
              >
                <Download className="w-5 h-5" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-center py-3 px-4 font-medium text-gray-700">#</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Customer Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Mobile No1</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Mobile No2</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Tel No</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Email Id</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Address</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Location</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Emirate</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center">
                      <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading customers...</p>
                    </td>
                  </tr>
                ) : filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center">
                      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {searchKeyword || emirateFilter !== 'all' ? 'No customers found' : 'No customers available'}
                      </h3>
                      <p className="text-gray-500">
                        {searchKeyword || emirateFilter !== 'all' 
                          ? 'Try adjusting your search criteria' 
                          : 'Customer data will appear here once available'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer, index) => (
                    <tr key={customer.Id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                      <td className="py-3 px-4 text-center text-sm text-gray-500 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mr-3">
                            <span className="text-xs font-semibold text-green-700">
                              {(customer.Name || '').split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900">{customer.Name || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700 font-mono">{customer.MobileNo1 || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700 font-mono">{customer.MobileNo2 || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700 font-mono">{customer.TelNo || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{customer.EmailId || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">{customer.Address || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{customer.LocationName || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {customer.EmirateName || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCustomers;
